import React from "react";
import { format } from "date-fns";
import { Typography, Input, Menu, Button } from "@material-tailwind/react";
import { useFormContext } from 'react-hook-form'

import TagInput from "./TagInput";
import { DatepickerInput } from "./DatapickerInput";
import { FormDatepickerInput } from "./FormDatepickerInput";
import FormColorPicker from "./FormColorPicker";
import { FormTagInput } from "./FormTagInput";
import FormInput from "./FormInput";
import FormCheckbox from "./FormCheckbox";
import FormFileUpload from "./FormFileUpload";
import { getMoneyFormat, getPercentFormat, getTextFormat } from "../utils/formatViews";
import { isFormInvalid, findInputError } from "../utils";

export interface Field {
    n: string;
    f: string;
    min?: number;
    max?: number;
    endMonth?: Date;
    startMonth?: Date;
    colspan?: number;
    type: string;
    rows?: number;
    placeholder?: string;
    input_type?: string;
    editable: boolean;
    accept?: string;
    options?: { n: string; f: string; v: string; type: string }[];
    type_field?: string;
    cardClass?: string;
    textClass?: string;
    show?: string;
    hide?: string;
    readonly?: boolean;
    required?: boolean;
    validation?: any;
}

export const getField = (field: string, object: Record<string, any>) => {
    if (object?.id) {
        if (field.indexOf(".") !== -1) {
            const [key, subkey] = field.split(".");
            return object[key]?.[subkey] || "";
        } else {
            const v = object[field] || "";
            if (typeof v === "object") {
                /*
                try {
                    // Format the JSON with proper indentation for better readability
                    return JSON.stringify(v, null, 4);
                } catch (error) {
                    // Fallback to simple stringification if there's an error
                    return JSON.stringify(v);
                }
                */
                return JSON.stringify(v);
            }
            return v;
        }
    }
};

export const getFileField = (field: string, object: Record<string, any>) => {
    const file = getField(field, object) || "";
    if (file) {
        return <a href={file} target="_blank" rel="noopener noreferrer">{file.split("/").pop()}</a>;
    }
    return "N/A";
};

export const getSelectField = (field: Field, object: Record<string, any>) => {
    const value = getField(field.f, object);
    const type = field.options?.find((el: any) => el.v === value);
    return type?.n || "N/A";
};

export const getCombinedField = (field: Field, object: Record<string, any>) => {
    const type = field.options?.find((el) => el.v === getField(field?.type_field || '', object));

    if (type?.type == 'money') {
        return getMoneyFormat(getField(field.f, object) as string);
    } else if (type?.type == 'percent') {
        return getPercentFormat(getField(field.f, object) as string);
    }

    return getTextFormat(getField(field.f, object) as string);
};


const handleChange = (field: string, value: string, type: string, setNewVehicle: (vehicle: Record<string, any>) => void) => {
    if (field.indexOf(".") !== -1) {
        const [key, subkey] = field.split(".");
        setNewVehicle((prev: Record<string, any>) => ({
            ...prev,
            [key]: {
                ...prev[key],
                [subkey]:
                    type === "money" || type === "number" || type === "percent"
                        ? Number(value)
                        : value,
            },
        }));
    } else {
        setNewVehicle((prev: Record<string, any>) => ({
            ...prev,
            [field]:
                type === "money" || type === "number" || type === "percent"
                    ? Number(value)
                    : value,
        }));
    }
};

interface RenderFormFieldProps {
    field: Field;
    isEdit: boolean;
    object: Record<string, any>;
    setNewObject: (object: Record<string, any>) => void;
}

export const RenderFormField: React.FC<RenderFormFieldProps> = ({
    field,
    isEdit,
    object,
    setNewObject
}) => {
    const {
        formState: { errors },
    } = useFormContext()

    const inputErrors = findInputError(errors, field.f)
    const isInvalid = isFormInvalid(inputErrors)

    return (
        <React.Fragment>
            <Typography
                type="small"
                className={`block ${isInvalid ? "text-red-500" : "text-foreground"}`}
            >
                {field.n}
                {field.required && <span>*</span>}
            </Typography>
            {isEdit && field.editable ? (
                field.type === "select" ? (
                    <FormTagInput
                        id={field.f}
                        name={field.f}
                        label=""
                        wrapperClass="mt-1 mb-6 w-full"
                        labelClass="mb-2 font-medium text-sm text-foreground"
                        placeholder={field.placeholder || "Select an option"}
                        options={
                            field.options?.map((option) => ({
                                value: option.v,
                                label: option.n,
                            })) || []
                        }
                        required={field.required}
                    />
                ) : field.type === "color" ? (
                    <FormColorPicker
                        name={field.f}
                        label=""
                        validation={{
                            pattern: {
                                value: /^#[0-9A-F]{6}$/i,
                                message: "Invalid hex color",
                            },
                        }}
                    />
                ) : field.type === "checkbox" ? (
                    <FormCheckbox
                        name={field.f}
                        label=""
                        value={getField(field.f, object) as string}
                    />
                ) : field.type === "date" ? (
                    <FormDatepickerInput
                        name={field.f}
                        label=""
                        startMonth={field?.startMonth ? field.startMonth : undefined}
                        endMonth={field?.endMonth ? field.endMonth : new Date()}
                        wrapperClass="mt-1 w-[95%]"
                        validation={
                            field.required
                                ? {
                                      required: {
                                          value: true,
                                          message: "This field is required",
                                      },
                                  }
                                : undefined
                        }
                        placeholder={field.placeholder}
                    />
                ) : field.type === "textarea" ? (
                    <FormInput
                        id={field.f}
                        name={field.f}
                        type="text"
                        rows={field.rows || 8}
                        multiline={true}
                        placeholder={field.n}
                        label=""
                        readonly={field.readonly}
                        validation={
                            field.required
                                ? {
                                      required: {
                                          value: true,
                                          message: "This field is required",
                                      },
                                  }
                                : undefined
                        }
                    />
                ) : field.type === "file" ? (
                    <FormFileUpload
                        minimal={true}
                        show_preview={false}
                        title=""
                        desc=""
                        accept="*/*"
                        name={field.f}
                        btnClass="!h-[40px]"
                    />
                ) : field.type === "combined_input" ? (
                    <div className="relative flex w-full max-w-[24rem]">
                        <Menu placement="bottom-start">
                            <Menu.Trigger
                                as={Button}
                                color="secondary"
                                className="flex items-center gap-2 rounded-r-none border border-r-0 border-surface bg-secondary pl-3"
                            >
                                {getField(field?.type_field || "", object) as string}
                            </Menu.Trigger>
                            <Menu.Content className="overflow-y-auto">
                                {field.options?.map((el, index) => {
                                    return (
                                        <Menu.Item
                                            key={index}
                                            value={el.v}
                                            className="flex items-center gap-2"
                                            onClick={() =>
                                                handleChange(
                                                    field?.type_field || "",
                                                    el.v,
                                                    "string",
                                                    setNewObject
                                                )
                                            }
                                        >
                                            {el.n}
                                        </Menu.Item>
                                    );
                                })}
                            </Menu.Content>
                        </Menu>
                        <FormInput
                            id={field.f}
                            name={field.f}
                            type={field.input_type || "text"}
                            placeholder={field.n}
                            label=""
                            validation={
                                field.required
                                    ? {
                                          required: {
                                              value: true,
                                              message: "This field is required",
                                          },
                                      }
                                    : undefined
                            }
                        />
                    </div>
                ) : (
                    <FormInput
                        id={field.f}
                        name={field.f}
                        type={field.type === "money" ? "number" : field.type}
                        placeholder={field.n}
                        label=""
                        min={field?.min}
                        max={field?.max}
                        readonly={field.readonly}
                        validation={
                            field.required
                                ? {
                                      required: {
                                          value: true,
                                          message: "This field is required",
                                      },
                                  }
                                : undefined
                        }
                    />
                )
            ) : (
                <Typography className="block text-primary md:text-lg font-bold overflow-hidden text-ellipsis">
                    {field.type === "text" ? (
                        getTextFormat(getField(field.f, object) as string)
                    ) : field.type === "textarea" ? (
                        <div className="max-h-[500px] overflow-y-auto">
                            {getField(field.f, object) as string}
                        </div>
                    ) : field.type === "file" ? (
                        getFileField(field.f, object)
                    ) : field.type === "checkbox" ? (
                        (getField(field.f, object) as string) ? (
                            "Yes"
                        ) : (
                            "No"
                        )
                    ) : field.type === "image" ? (
                        (getField(field.f, object) as string) ? (
                            <img
                                src={getField(field.f, object) as string}
                                alt="signature"
                                className="w-auto h-full max-h-16 object-contain"
                            />
                        ) : (
                            "N/A"
                        )
                    ) : field.type === "select" ? (
                        getSelectField(field, object)
                    ) : field.type === "color" ? (
                        <div className="flex items-center gap-2">
                            <div
                                className="w-8 h-8 rounded border border-gray-300"
                                style={{ backgroundColor: getField(field.f, object) as string }}
                            />
                            {getField(field.f, object) || "N/A"}
                        </div>
                    ) : field.type === "money" ? (
                        getMoneyFormat(getField(field.f, object) as string)
                    ) : field.type === "combined_input" ? (
                        getCombinedField(field, object)
                    ) : (
                        getField(field.f, object) || "N/A"
                    )}
                </Typography>
            )}
        </React.Fragment>
    );
};

export const RenderField = (
    field: Field,
    isEdit: boolean,
    object: Record<string, any>,
    setNewVehicle: (vehicle: Record<string, any>) => void
) => {
    return (
        <React.Fragment>
            <Typography type="small" className="block text-foreground">
                {field.n}
            </Typography>
            {isEdit && field.editable ? (
                field.type === "select" ? (
                    <TagInput
                        id={field.f}
                        value={getField(field.f, object) as string}
                        setValue={(value: string) => {
                            handleChange(field.f, value, "string", setNewVehicle);
                        }}
                        label=""
                        wrapperClass="mt-1 w-[95%]"
                        placeholder={field.placeholder || "Select an option"}
                        options={
                            field.options?.map((option) => ({
                                value: option.v,
                                label: option.n,
                            })) || []
                        }
                    />
                ) : field.type === "date" ? (
                    <DatepickerInput
                        wrapperClass="mt-1 w-[95%]"
                        date={
                            object[field.f]
                                ? new Date(object[field.f] + 'T00:00:00')
                                : null
                        }
                        setDate={(date: Date) => {
                            handleChange(
                                field.f,
                                format(date, "yyyy-MM-dd"),
                                "date",
                                setNewVehicle
                            );
                        }}
                    />
                ) : field.type === "file" ? (
                    <FormFileUpload
                        show_preview={false}
                        title={field.n}
                        desc=""
                        accept="*/*"
                        name={field.f}
                        btnClass="!h-[40px]"
                    />
                ) : field.type === "combined_input" ? (
                    <div className="relative flex w-full max-w-[24rem]">
                        <Menu placement="bottom-start">
                            <Menu.Trigger
                                as={Button}
                                color="secondary"
                                className="flex items-center gap-2 rounded-r-none border border-r-0 border-surface bg-secondary pl-3"
                            >
                                {getField(field?.type_field || "", object) as string}
                            </Menu.Trigger>
                            <Menu.Content className="overflow-y-auto">
                                {field.options?.map((el, index) => {
                                    return (
                                        <Menu.Item
                                            key={index}
                                            value={el.v}
                                            className="flex items-center gap-2"
                                            onClick={() =>
                                                handleChange(
                                                    field?.type_field || "",
                                                    el.v,
                                                    "string",
                                                    setNewVehicle
                                                )
                                            }
                                        >
                                            {el.n}
                                        </Menu.Item>
                                    );
                                })}
                            </Menu.Content>
                        </Menu>
                        <Input
                            placeholder={field.n}
                            type={field.input_type}
                            className="rounded-l-none"
                            value={getField(field.f, object) as string}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handleChange(
                                    field.f,
                                    e.target.value,
                                    field.input_type || "",
                                    setNewVehicle
                                )
                            }
                        />
                    </div>
                ) : (
                    <Input
                        type={field.type === "money" ? "number" : field.type}
                        className="mt-1 w-[95%]"
                        value={getField(field.f, object) as string}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange(field.f, e.target.value, field.type, setNewVehicle)
                        }
                        min={field?.min}
                        max={field?.max}
                    />
                )
            ) : (
                <Typography className="block text-primary md:text-lg font-bold overflow-hidden text-ellipsis">
                    {field.type === "text" ? (
                        getTextFormat(getField(field.f, object) as string)
                    ) : field.type === "image" ? (
                        (getField(field.f, object) as string) ? (
                            <img
                                src={getField(field.f, object) as string}
                                alt="signature"
                                className="w-auto h-full max-h-16 object-contain"
                            />
                        ) : (
                            "N/A"
                        )

                    ) : field.type === "select" ? (
                        getSelectField(field, object)
                    ) : field.type === "file" ? (
                        getField(field.f, object) || "N/A"
                    ) : field.type === "money" ? (
                        getMoneyFormat(getField(field.f, object) as string)
                    ) : field.type === "combined_input" ? (
                        getCombinedField(field, object)
                    ) : (
                        getField(field.f, object) || "N/A"
                    )}
                </Typography>
            )}
        </React.Fragment>
    );
};

export default RenderField;